import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft, ChevronLeft, Clock, Disc3, MapPin, MessageCircle, Phone, Radar,
  Repeat, Scale, ShieldCheck, Truck, Wind, Wrench,
} from "lucide-react";
import SEO, { faqLd } from "@/components/SEO";
import Faq from "@/components/Faq";
import { Reveal, Marquee } from "@/components/Reveal";
import { LocationWhatsAppButton, WA_SERVICE_CHIPS } from "@/components/WhatsAppCTA";
import { BUSINESS, WA_DEFAULT, WA_ORDER, waLink } from "@/data/business";
import { IMG } from "@/data/services";
import { AREAS } from "@/data/areas";
import { ARTICLES } from "@/data/blog";

const SERVICE_CARDS = [
  { icon: Wrench, label: "إصلاح البنشر", desc: "إغلاق الثقوب والإصلاح الميداني دون فك العجلة من مكانها", to: "/بنشر-متنقل-الرياض" },
  { icon: Repeat, label: "تغيير وتركيب الكفرات", desc: "فك وتركيب في موقعك مع موازنة كل عجلة", to: "/تغيير-كفرات-متنقل" },
  { icon: Truck, label: "تجهيز كفرات جديدة", desc: "نوفر لك الكفر المناسب لمقاس سيارتك ونركبه في مكانك", to: "/كفرات-الرياض" },
  { icon: Disc3, label: "تركيب الجنوط", desc: "جنوط بمقاسات 16-19 بوصة مع فحص توافق كامل", to: "/جنوط-الرياض" },
  { icon: Radar, label: "برمجة حساسات TPMS", desc: "قراءة الحساسات وبرمجتها وإطفاء لمبة التحذير", to: "/برمجة-حساسات-الكفرات" },
  { icon: Scale, label: "ترصيص الإطارات", desc: "معالجة اهتزاز المقود بجهاز موازنة متنقل", to: "/خدمة-الإطارات-المتنقلة" },
  { icon: Wind, label: "فحص وتعبئة الهواء", desc: "ضبط الضغط الصحيح لكل كفر حسب مواصفات سيارتك", to: "/بنشر-متنقل-الرياض" },
  { icon: MapPin, label: "بنشر متنقل على الطريق", desc: "على الطريق السريع أو داخل الحي — نصل إليك 24 ساعة", to: "/بنشر-متنقل-قريب-مني" },
];

const STEPS = [
  { n: "01", t: "راسلنا على واتساب", d: "اضغط «اطلب بنشر متنقل» — تفتح محادثة واتساب جاهزة" },
  { n: "02", t: "أرسل موقعك", d: "زر «أرسل موقعك للفني» يفتح واتساب مع رابط موقعك تلقائياً" },
  { n: "03", t: "نصل إليك", d: "الفني المجهز يصل إلى موقعك أينما كنت في الرياض" },
  { n: "04", t: "ستكمل رحلتك", d: "الخدمة تُنجز في الموقع — لا ورشة ولا انتظار" },
];

const HOME_FAQS = [
  { q: "هل تصلون إلى جميع مناطق الرياض؟", a: "نعم، نغطي الرياض كاملة: الشمال والجنوب والشرق والغرب والوسط — الأحياء السكنية والطرق الرئيسية والمواقف." },
  { q: "هل الخدمة متاحة 24 ساعة؟", a: "نعم، نعمل على مدار الساعة طوال أيام الأسبوع — بما في ذلك الليالي ونهاية الأسبوع والأعياد." },
  { q: "كيف أطلب الخدمة؟", a: "عبر واتساب فقط: اضغط «اطلب بنشر متنقل» أو «أرسل موقعك للفني» — تفتح محادثة واتساب جاهزة بالتفاصيل وموقعك." },
  { q: "هل توفرنون كفرات جديدة؟", a: "نعم، نوفر كفرات بماركات معروفة بمقاس سيارتك ونركبها في موقعك مع الموازنة — استفسر عن المتوفر عبر واتساب." },
  { q: "كيف أرسل موقعي للفني؟", a: "اضغط زر «أرسل موقعك للفني» فيفتح واتساب برسالة تحتوي رابط موقعك الحالي من خرائط جوجل تلقائياً." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden grain">
      <motion.div style={{ y: bgY, scale: 1.1 }} className="absolute inset-0" data-testid="hero-parallax">
        <img
          src={IMG.hero}
          alt="فني بنشر متنقل يصلح كفر سيارة على الطريق في الرياض"
          fetchPriority="high"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-l from-ink/95 via-ink/70 to-ink/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      <div className="absolute inset-0 spotlight" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-24 pb-16">
        <div className="fade-up-load max-w-2xl" style={{ animationDelay: "0.1s" }}>
          <span className="inline-flex items-center gap-2 bg-gold/10 border border-gold/40 text-gold-soft text-xs font-bold rounded-full px-4 py-1.5" data-testid="hero-badge">
            <Clock size={13} />
            خدمة 24 ساعة — جميع مناطق الرياض
          </span>
        </div>

        <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.15] mt-6" data-testid="hero-h1">
          <span className="mask-line"><span style={{ animationDelay: "0.15s" }}>بنشر متنقل</span></span>
          <span className="mask-line"><span className="text-gold" style={{ animationDelay: "0.3s" }}>نجم الإطار</span></span>
          <span className="mask-line"><span style={{ animationDelay: "0.45s" }}>في الرياض</span></span>
        </h1>

        <p className="fade-up-load mt-6 text-lg sm:text-xl text-gray-200 font-medium leading-relaxed" style={{ animationDelay: "0.65s" }} data-testid="hero-tagline">
          تعطلت؟ لا توقف رحلتك.
          <br />
          <span className="text-gold-soft font-bold">نجم الإطار يصلك إلى موقعك.</span>
        </p>

        <div className="fade-up-load mt-8 flex flex-wrap gap-3" style={{ animationDelay: "0.8s" }}>
          <a
            href={WA_ORDER}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-cta"
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-ink font-black text-base rounded-full px-7 py-3.5 transition-all duration-300 hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(245,158,11,0.35)]"
          >
            <Truck size={18} />
            اطلب بنشر متنقل
          </a>
          <LocationWhatsAppButton
            testid="hero-location-cta"
            label="أرسل موقعك"
            className="inline-flex items-center gap-2 border border-gold/50 text-gold-soft hover:bg-gold hover:text-ink font-bold rounded-full px-7 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
          />
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-wa hover:bg-wa-deep text-white font-bold rounded-full px-7 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} />
            واتساب
          </a>
          <a
            href={BUSINESS.telLink}
            data-testid="hero-call-cta"
            className="inline-flex items-center gap-2 border border-blood/60 text-white hover:bg-blood font-bold rounded-full px-7 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
          >
            <Phone size={18} />
            اتصل الآن
            <span dir="ltr" className="font-mono text-sm text-gray-300">{BUSINESS.phoneDisplay}</span>
          </a>
        </div>

        <div className="fade-up-load mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-300" style={{ animationDelay: "0.95s" }}>
          <span className="flex items-center gap-2"><Clock size={15} className="text-gold" /> على مدار 24 ساعة</span>
          <span className="flex items-center gap-2"><MapPin size={15} className="text-gold" /> الرياض كاملة — شمالاً وجنوباً</span>
          <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-gold" /> إصلاح وتغيير وترصيص في الموقع</span>
        </div>
      </motion.div>
    </section>
  );
};

const CtaBand = ({ title = "تعطلت الآن؟ لا توقف رحلتك", testid = "cta-band" }) => (
  <section className="relative overflow-hidden bg-gold" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 flex flex-col lg:flex-row items-center justify-between gap-8">
      <div className="text-center lg:text-start">
        <h2 className="font-display font-black text-3xl sm:text-4xl text-ink leading-snug">{title}</h2>
        <p className="text-ink/80 font-semibold mt-2">نجم الإطار يصلك إلى موقعك — 24 ساعة في الرياض</p>
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        <a href={WA_ORDER} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-order`} className="inline-flex items-center gap-2 bg-ink text-gold-soft font-black rounded-full px-7 py-3.5 hover:bg-panel2 transition-colors">
          <Truck size={18} /> اطلب بنشر متنقل
        </a>
        <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-whatsapp`} className="inline-flex items-center gap-2 bg-wa hover:bg-wa-deep text-white font-black rounded-full px-7 py-3.5 transition-colors">
          <MessageCircle size={18} /> واتساب
        </a>
        <a href={BUSINESS.telLink} data-testid={`${testid}-call`} className="inline-flex items-center gap-2 bg-blood hover:bg-blood-deep text-white font-black rounded-full px-7 py-3.5 transition-colors">
          <Phone size={18} /> <span dir="ltr">{BUSINESS.phoneDisplay}</span>
        </a>
        <LocationWhatsAppButton
          testid={`${testid}-location`}
          label="أرسل موقعك للفني"
          className="inline-flex items-center gap-2 border-2 border-ink/30 text-ink font-black rounded-full px-7 py-3.5 hover:bg-ink hover:text-gold-soft transition-colors"
        />
      </div>
    </div>
  </section>
);

export { CtaBand };

const SectionHead = ({ eyebrow, title, desc }) => (
  <Reveal className="max-w-2xl mb-12">
    <span className="text-gold-soft font-bold text-sm tracking-widest">{eyebrow}</span>
    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-tight">{title}</h2>
    {desc && <p className="text-gray-400 mt-4 leading-relaxed">{desc}</p>}
  </Reveal>
);

const Home = () => {
  useEffect(() => {}, []);

  return (
    <>
      <SEO
        title="بنشر متنقل نجم الإطار | بنشر متنقل الرياض وإطارات وجنوط"
        description="بنشر متنقل نجم الإطار في الرياض — إصلاح البنشر، تغيير وتركيب الكفرات، تركيب الإطارات والجنوط، برمجة حساسات TPMS وترصيص. نصل إليك أينما كنت 24 ساعة. واتساب 0538644740"
        path="/"
        image={IMG.hero}
        jsonLd={[faqLd(HOME_FAQS)]}
      />
      <Hero />
      <Marquee />

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <SectionHead
          eyebrow="خدماتنا"
          title="كل ما يحتاجه إطار سيارتك — في موقعك"
          desc="ورشة متكاملة على متن سيارة البنشر المتنقل: من إصلاح الثقب إلى تركيب الجنوط وبرمجة الحساسات — والطلب كله عبر واتساب."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICE_CARDS.map((s, i) => (
            <Reveal key={s.label} delay={(i % 4) * 0.08}>
              <Link
                to={s.to}
                data-testid={`service-card-${i}`}
                className="group h-full bg-panel border border-edge hover:border-gold/50 rounded-2xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-1.5"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gold/10 border border-gold/30 text-gold group-hover:bg-gold group-hover:text-ink transition-colors duration-300 mb-5">
                  <s.icon size={22} />
                </span>
                <h3 className="font-display font-bold text-white text-lg mb-2">{s.label}</h3>
                <p className="text-sm text-gray-400 leading-relaxed flex-1">{s.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-gold-soft text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  التفاصيل <ArrowLeft size={15} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="bg-panel border-y border-edge py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead eyebrow="كيف نعمل" title="أربع خطوات بين التعطل والانطلاق" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            <div className="hidden lg:block absolute top-8 inset-x-12 border-t border-dashed border-gold/30" />
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12} className="relative">
                <div className="relative z-10 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-ink border border-gold/40 font-mono text-xl font-bold text-gold mb-5">
                  {s.n}
                </div>
                <h3 className="font-display font-bold text-white text-xl mb-2">{s.t}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Van split */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 border border-gold/30 rounded-3xl translate-x-4 translate-y-4" />
              <div className="relative overflow-hidden rounded-3xl card-clip">
                <img src={IMG.van} alt="فانيلا نجم الإطار — البنشر المتنقل الرسمية في الرياض" loading="lazy" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 start-6 end-6">
                  <span className="text-gold-soft font-bold text-sm">مجهزة بالكامل</span>
                  <p className="text-white font-display font-bold text-xl">أجهزة فك وتركيب وموازنة وترصيص على متن السيارة</p>
                </div>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHead
              eyebrow="لماذا نجم الإطار؟"
              title="نصلك أينما كنت — بيتك، عملك، أو على الطريق"
              desc="بدأنا بهذا الشعار وبنينا عليه كل شيء: لا ورشة تنتظرك، بل فني محترف يصل إلى موقعك بمعدات كاملة ويعود برحلتك إلى مسارها."
            />
            <div className="space-y-4">
              {[
                { icon: ShieldCheck, t: "معدات كاملة على متن السيارة", d: "رفع، فك وتركيب، موازنة، ترصيص، وأجهزة برمجة TPMS" },
                { icon: Clock, t: "مواعيد تراعي يومك", d: "صباحاً، مساءً، أو في منتصف الليل — الخدمة تعمل 24 ساعة" },
                { icon: MapPin, t: "تغطية الرياض كاملة", d: "من حطين والملقا شمالاً إلى الشفا جنوباً، ومن السويدي غرباً إلى النسيم شرقاً" },
              ].map((f, i) => (
                <Reveal key={f.t} delay={i * 0.1}>
                  <div className="flex gap-4 bg-panel border border-edge rounded-2xl p-5 hover:border-gold/40 transition-colors">
                    <span className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 border border-gold/30 text-gold">
                      <f.icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-bold text-white">{f.t}</h3>
                      <p className="text-sm text-gray-400 mt-1 leading-relaxed">{f.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Areas */}
      <section id="areas" className="bg-panel border-y border-edge py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead
            eyebrow="مناطق الخدمة"
            title="نخدم الرياض كاملة — من الشمال إلى الجنوب"
            desc="فريقنا يتحرك في جميع مناطق المدينة. اختر منطقتك لتفاصيل التغطية فيها."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {AREAS.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.07}>
                <Link
                  to={`/${a.slug}`}
                  data-testid={`area-card-${a.slug}`}
                  className="group block h-full bg-ink border border-edge hover:border-gold/50 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1.5"
                >
                  <MapPin size={22} className="text-gold mb-4" />
                  <h3 className="font-display font-bold text-white text-xl mb-2">{a.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{a.districts.slice(0, 3).join(" · ")}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-gold-soft text-sm font-bold">
                    التغطية في المنطقة <ChevronLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />

      {/* Blog preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <SectionHead
            eyebrow="دليل الإطارات"
            title="مقالات تساعدك تفهم كفراتك أكثر"
            desc="محتوى عملي من فريق يتعامل مع الإطارات كل يوم في شوارع الرياض."
          />
          <Reveal>
            <Link to="/المدونة" data-testid="blog-preview-link" className="inline-flex items-center gap-2 text-gold-soft font-bold border border-gold/40 hover:bg-gold hover:text-ink rounded-full px-6 py-3 transition-colors mb-12">
              كل المقالات <ArrowLeft size={16} />
            </Link>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {ARTICLES.slice(0, 3).map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.1}>
              <Link
                to={`/المدونة/${a.slug}`}
                data-testid={`blog-preview-card-${i}`}
                className="group block bg-panel border border-edge hover:border-gold/50 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 h-full"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.image} alt={a.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-white text-lg leading-snug group-hover:text-gold-soft transition-colors">{a.title}</h3>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-gold-soft text-sm font-bold">
                    اقرأ المقال <ChevronLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ + WhatsApp order */}
      <section className="bg-panel border-t border-edge py-24" id="order">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHead
              eyebrow="أسئلة شائعة"
              title="كل ما تسأل عنه قبل أن تطلب"
            />
            <Faq items={HOME_FAQS} testidPrefix="home-faq" />
          </div>
          <div className="text-center lg:text-start">
            <SectionHead
              eyebrow="اطلب الآن"
              title="الطلب عبر واتساب — بضغطة واحدة"
              desc="اختر خدمتك واضغط — تفتح محادثة واتساب جاهزة. أو أرسل موقعك للفني برسالة واحدة."
            />
            <Reveal>
              <div className="flex flex-wrap justify-center lg:justify-start gap-2.5 mb-8">
                {WA_SERVICE_CHIPS.map((s) => (
                  <a
                    key={s}
                    href={waLink(`مرحباً، أحتاج خدمة: ${s} في الرياض.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`wa-quick-${s}`}
                    className="border border-edge hover:border-gold/60 hover:text-gold-soft text-gray-300 rounded-full px-5 py-2.5 text-sm transition-colors"
                  >
                    {s}
                  </a>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <a
                  href={WA_ORDER}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-wa-order-btn"
                  className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-ink font-black rounded-full px-8 py-4 transition-colors"
                >
                  <Truck size={18} /> اطلب بنشر متنقل الآن
                </a>
                <LocationWhatsAppButton
                  testid="home-location-whatsapp"
                  label="أرسل موقعي للفني"
                  className="inline-flex items-center justify-center gap-2 border border-gold/50 text-gold-soft hover:bg-gold hover:text-ink font-bold rounded-full px-8 py-4 transition-colors"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
